<script lang="ts" setup>
const emit = defineEmits(["close"]);
const props = defineProps({
  type: {
    type: String as PropType<MFAType>,
    required: true,
  },
});

const { toaster } = useToaster();
const { updateWalletKeys } = useUserController();
const userStore = useUserStore();
const showKeysSocialAccount = ref(false);
const showKeysRecoveryPhraseIntro = ref(false);
const showKeysRecoveryPhrase = ref(false);
const showKeysEnd = ref(false);
const isLoading = ref(false);
const isPreparingRescue = ref(true);
const { ensureRegisteredRescuePublicKey } = useRescueController();

const encryptAndUpdateKeys = async ({
  method,
  value,
}: {
  method: UserKeyMethod;
  value: string;
}) => {
  try {
    showKeysRecoveryPhrase.value = false;
    showKeysSocialAccount.value = false;
    isLoading.value = true;
    const response = await updateWalletKeys({
      method,
      value,
      password: null,
    });
    if (response.statusCode === 200) {
      showKeysSocialAccount.value = false;
      showKeysRecoveryPhrase.value = false;
      showKeysEnd.value = true;
    } else {
      throw response;
    }
    isLoading.value = false;
  } catch (error) {
    console.error("Error updating keys", error);
    toaster("error");
    emit("close");
  }
};

const handleIntroContinue = () => {
  showKeysRecoveryPhraseIntro.value = false;
  showKeysRecoveryPhrase.value = true;
};

onMounted(async () => {
  try {
    await ensureRegisteredRescuePublicKey({
      intent: {
        action: "update-wallet-keys",
        modal: "keys-settings",
        type: props.type,
      },
    });
    isPreparingRescue.value = false;
    if (props.type === "SOCIAL") {
      showKeysSocialAccount.value = true;
    } else if (props.type === "RECOVERY") {
      if (userStore.recoveryKitDate) {
        showKeysRecoveryPhraseIntro.value = true;
      } else {
        showKeysRecoveryPhrase.value = true;
      }
    } else {
      emit("close");
    }
  } catch (error) {
    if ((error as Error)?.name === "RescueRegistrationRedirectedError") return;
    console.error("Error preparing rescue key registration", error);
    emit("close");
  }
});
</script>

<template>
  <BaseLayoutModal width="xl" @close="$emit('close')">
    <template #header>
      {{
        type === "SOCIAL"
          ? $t("main.keysSettings.connectSocialAccounts")
          : $t("main.keysSettings.walletRecoveryKit")
      }}
    </template>
    <template #content>
      <Transition mode="out-in">
        <ModalLoader
          v-if="isPreparingRescue"
          :body-title="$t('main.keys.updatingKeys')"
          :body-subtitle="$t('main.keys.waitWhileUpdatingKeys')" />
        <ModalKeysSettingsSocialAccountSettings
          v-else-if="showKeysSocialAccount"
          @continue=""
          @skip=""
          @close="$emit('close')" />
        <ModalKeysRecoveryPhraseIntro
          v-else-if="showKeysRecoveryPhraseIntro"
          @continue="handleIntroContinue"
          @close="$emit('close')" />
        <ModalKeysRecoveryPhrase
          v-else-if="showKeysRecoveryPhrase"
          @continue="encryptAndUpdateKeys"
          @back="$emit('close')" />
        <ModalLoader
          v-else-if="isLoading"
          :body-title="$t('main.keys.updatingKeys')"
          :body-subtitle="$t('main.keys.waitWhileUpdatingKeys')" />
        <ModalKeysRecoveryPhraseSuccess
          v-else-if="showKeysEnd"
          @close="$emit('close')" />
      </Transition>
    </template>
  </BaseLayoutModal>
</template>
